export interface BusinessConfig {
  businessName: string;
  welcomeMessage: string;
  chatPlaceholder: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
  };
  ai: {
    model: string;
    temperature: number;
    maxTokens: number;
    systemPrompt: string;
  };
  booking?: {
    enabled: boolean;
    baseUrl: string;
    modalTarget?: string;
    tours?: {
      [key: string]: {
        name: string;
        bookingUrl: string;
        price: string;
        duration: string;
        keywords: string[];
        aliases: string[];
        category: string;
        priority: number;
      };
    };
    fallback: {
      phone: string;
      email: string;
      generalBookingUrl: string;
    };
    buttonStyle: {
      primary: string;
      secondary: string;
      text: string;
    };
  };
  rateLimiting: {
    enabled: boolean;
    maxRequestsPerHour: number;
    maxRequestsPerDay: number;
  };
  notifications: {
    email: string;
    newChatAlert: boolean;
    dailyReport: boolean;
  };
  rag: {
    enabled: boolean;
    maxResults: number;
    similarityThreshold: number;
  };
  ui?: {
    showWelcomeMessage: boolean;
    showTypingIndicator: boolean;
    showSourceInfo: boolean;
    maxChatHistory: number;
    autoScrollChat: boolean;
  };
  landing?: {
    title: string;
    subtitle: string;
    testCardTitle: string;
    testCardDescription: string;
    testButtonText: string;
    adminCardTitle: string;
    adminCardDescription: string;
    adminButtonText: string;
    footer: string;
  };
  admin?: {
    defaultEmail: string;
    sessionTimeout: number;
    maxExportRows: number;
    adminUrl?: string;
  };
}

class ConfigService {
  private config: BusinessConfig | null = null;
  private loadingPromise: Promise<BusinessConfig> | null = null;

  async getConfig(): Promise<BusinessConfig> {
    if (this.config) {
      return this.config;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.loadConfig();
    return this.loadingPromise;
  }

  private async loadConfig(): Promise<BusinessConfig> {
    try {
      // Force production for now since user wants real data
      const baseUrl = 'https://us-central1-adriftchatbotv3.cloudfunctions.net';

      const response = await fetch(`${baseUrl}/getConfig`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const config = await response.json();
      this.config = config;
      return config;
    } catch (error) {
      console.error('Failed to load config:', error);
      // Return a default config if loading fails
      return {
        businessName: 'Adrift Tongariro',
        welcomeMessage: 'Welcome to Adrift Tongariro! How can I help you with our guided outdoor adventures?',
        chatPlaceholder: 'Ask about our guided walks, hikes, and outdoor tours...',
        theme: {
          primaryColor: '#ff7f00',
          secondaryColor: '#6c757d',
          backgroundColor: '#ffffff',
          textColor: '#333333'
        },
        ai: {
          model: 'gpt-3.5-turbo',
          temperature: 0.7,
          maxTokens: 1000,
          systemPrompt: 'You are a helpful AI assistant for Adrift Tongariro.'
        },
        booking: {
          enabled: true,
          baseUrl: 'https://adriftnz.co.nz',
          modalTarget: '#booking-modal',
          tours: {
            'sunset-walk': {
              name: '2 Hour Pre Sunset Guided Walk',
              bookingUrl: 'https://adriftnz.co.nz/guided-tongariro-walks/sunset-short-walk/',
              price: '$195pp',
              duration: '2-2.5 hours',
              keywords: ['sunset', 'short', 'easy', 'family', 'beginner', '2 hour', 'evening'],
              aliases: ['sunset walk', 'short walk', 'easy walk', 'family walk'],
              category: 'easy',
              priority: 4
            },
            'half-day-tongariro': {
              name: 'Half Day Tongariro Alpine Guided Walk',
              bookingUrl: 'https://adriftnz.co.nz/guided-tongariro-walks/half-day-walk/',
              price: '$350pp',
              duration: '3-3.5 hours',
              keywords: ['half day', 'tongariro', 'alpine', 'intermediate', 'soda springs', '3 hour'],
              aliases: ['half day', 'half day walk', 'tongariro half day', 'soda springs'],
              category: 'intermediate',
              priority: 3
            },
            'tongariro-crossing': {
              name: 'Premium Tongariro Crossing Guided Walk',
              bookingUrl: 'https://adriftnz.co.nz/guided-tongariro-walks/tongariro-alpine-crossing/',
              price: '$425pp',
              duration: '7-9 hours',
              keywords: ['tongariro', 'crossing', 'alpine', 'full day', 'emerald lakes', 'red crater', 'best', 'popular'],
              aliases: ['tongariro crossing', 'alpine crossing', 'full day', 'crossing', 'best walk'],
              category: 'full-day',
              priority: 1
            },
            'private-tongariro-crossing': {
              name: 'Private Tongariro Crossing Guided Walk',
              bookingUrl: 'https://adriftnz.co.nz/guided-tongariro-walks/tongariro-alpine-crossing-private/',
              price: '$2,550 total (1-3 pax)',
              duration: '7-9 hours',
              keywords: ['private', 'tongariro', 'crossing', 'alpine', 'exclusive', 'personal', 'group'],
              aliases: ['private crossing', 'private tongariro', 'exclusive crossing'],
              category: 'private',
              priority: 2
            },
            'ruapehu-summit': {
              name: 'Premium Mt Ruapehu Summit Plateau Guided Hike',
              bookingUrl: 'https://adriftnz.co.nz/guided-tongariro-walks/ruapehu-summit-plateau/',
              price: '$490pp',
              duration: '6-8 hours',
              keywords: ['ruapehu', 'summit', 'plateau', 'advanced', 'challenging', 'mountain', 'alpine'],
              aliases: ['ruapehu', 'summit', 'mountain', 'advanced hike', 'challenging'],
              category: 'advanced',
              priority: 5
            },
            'private-ruapehu-summit': {
              name: 'Private Mt Ruapehu Summit Plateau Guided Hike',
              bookingUrl: 'https://adriftnz.co.nz/guided-tongariro-walks/ruapehu-summit-plateau-private/',
              price: '$2,550 total (1-3 pax)',
              duration: '6-8 hours',
              keywords: ['private', 'ruapehu', 'summit', 'plateau', 'advanced', 'exclusive', 'mountain'],
              aliases: ['private ruapehu', 'private summit', 'exclusive ruapehu'],
              category: 'private',
              priority: 6
            },
            'whanganui-canoe': {
              name: 'Private Whanganui River Guided Canoe Tour',
              bookingUrl: 'https://adriftnz.co.nz/guided-whanganui-river-canoeing/one-day-private-tour/',
              price: '$2,550 total (1-3 pax)',
              duration: 'Full day',
              keywords: ['whanganui', 'river', 'canoe', 'kayak', 'private', 'historic', 'water'],
              aliases: ['whanganui', 'canoe', 'river', 'kayak', 'water tour'],
              category: 'water',
              priority: 7
            }
          },
          fallback: {
            phone: '+64 7 892 2751',
            email: 'bookings@adriftnz.co.nz',
            generalBookingUrl: 'https://adriftnz.co.nz/contact-us/'
          },
          buttonStyle: {
            primary: '#ff7f00',
            secondary: '#6c757d',
            text: '#ffffff'
          }
        },
        rateLimiting: {
          enabled: true,
          maxRequestsPerHour: 100,
          maxRequestsPerDay: 500
        },
        notifications: {
          email: 'geoff@emertech.nz',
          newChatAlert: true,
          dailyReport: true
        },
        rag: {
          enabled: true,
          maxResults: 5,
          similarityThreshold: 0.1
        },
        ui: {
          showWelcomeMessage: true,
          showTypingIndicator: true,
          showSourceInfo: false,
          maxChatHistory: 50,
          autoScrollChat: true
        },
        landing: {
          title: 'Adrift Tongariro AI Assistant',
          subtitle: 'Welcome to Adrift Tongariro! Test our AI assistant to discover our guided outdoor adventures and tours.',
          testCardTitle: 'Test the Chatbot',
          testCardDescription: 'Ask questions about Adrift Tongariro\'s guided walks, hikes, mountain biking, and canoe tours. Test various scenarios to ensure accurate responses.',
          testButtonText: 'Start Testing',
          adminCardTitle: 'Admin Panel', 
          adminCardDescription: 'Review conversations, rate responses, and monitor the chatbot\'s performance during testing.',
          adminButtonText: 'Open Admin Panel',
          footer: 'Powered by Adrift Tongariro'
        },
        admin: {
          defaultEmail: 'geoff@emertech.nz',
          sessionTimeout: 3600,
          maxExportRows: 10000
        }
      };
    }
  }

  // Clear cache to force reload
  clearCache(): void {
    this.config = null;
    this.loadingPromise = null;
  }
}

export const configService = new ConfigService();
export default configService; 